//#region Imports
import enLocale from "@anydo/i18n-texts/texts/en.json";
import noTranslate from "@anydo/i18n-texts/texts/no-translate.json";
import { timeFormatters as defaultTimeFormatters } from "@anydo/i18n-texts/time-formatters/en.time-formatters";
import { logError } from "@anydo/core";
import {
  type II18nLocaleLoader,
  type I18nLocaleDataT,
  type I18nLocaleLoaderLoadLanguageMessagesOptionsT,
  type SupportedLanguageCodeT,
  SUPPORTED_LANGUAGE_CODES,
} from "@anydo/interfaces";
import { SHOULD_POLYFILL_INTL, prepareI18nDependencies } from "./prepareI18nDependencies";
//#endregion

//#region Types
type I18nLocaleLoaderOptionsT = {
  detectedLocale: string;
  storage: Storage | undefined;
};
//#endregion

//#region Config

export const LOCAL_STORAGE_LANGUAGE_CODE_KEY = "anydo:language-code";
export const DEFAULT_LANGUAGE_CODE: SupportedLanguageCodeT = "en";

//#region Helpers
export function parseLocaleForLanguageCode(locale: string): SupportedLanguageCodeT {
  let languageCode = locale.replace(/_/g, "-").toLowerCase();

  let index = SUPPORTED_LANGUAGE_CODES.map((code) => code.toLowerCase()).indexOf(languageCode);
  if (index >= 0) {
    return SUPPORTED_LANGUAGE_CODES[index];
  }

  languageCode = languageCode.split("-")[0];
  index = SUPPORTED_LANGUAGE_CODES.map((code) => code.toLowerCase()).indexOf(languageCode);
  if (index >= 0) {
    return SUPPORTED_LANGUAGE_CODES[index];
  }

  const truncatedCodes = SUPPORTED_LANGUAGE_CODES.map((code) => code.split("-")[0].toLowerCase());
  index = truncatedCodes.indexOf(languageCode);
  if (index >= 0) {
    return SUPPORTED_LANGUAGE_CODES[index];
  }

  return DEFAULT_LANGUAGE_CODE;
}

function languageImports(languageCode: string) {
  switch (languageCode) {
    case "ar":
      return SHOULD_POLYFILL_INTL ? import("./imports/with-polyfills/ar") : import("./imports/ar");
    case "cs":
      return SHOULD_POLYFILL_INTL ? import("./imports/with-polyfills/cs") : import("./imports/cs");
    case "da":
      return SHOULD_POLYFILL_INTL ? import("./imports/with-polyfills/da") : import("./imports/da");
    case "de":
      return SHOULD_POLYFILL_INTL ? import("./imports/with-polyfills/de") : import("./imports/de");
    case "es":
      return SHOULD_POLYFILL_INTL ? import("./imports/with-polyfills/es") : import("./imports/es");
    case "fr":
      return SHOULD_POLYFILL_INTL ? import("./imports/with-polyfills/fr") : import("./imports/fr");
    case "he":
      return SHOULD_POLYFILL_INTL ? import("./imports/with-polyfills/he") : import("./imports/he");
    case "hu":
      return SHOULD_POLYFILL_INTL ? import("./imports/with-polyfills/hu") : import("./imports/hu");
    case "id":
      return SHOULD_POLYFILL_INTL ? import("./imports/with-polyfills/id") : import("./imports/id");
    case "it":
      return SHOULD_POLYFILL_INTL ? import("./imports/with-polyfills/it") : import("./imports/it");
    case "ja":
      return SHOULD_POLYFILL_INTL ? import("./imports/with-polyfills/ja") : import("./imports/ja");
    case "ko":
      return SHOULD_POLYFILL_INTL ? import("./imports/with-polyfills/ko") : import("./imports/ko");
    case "no":
      return SHOULD_POLYFILL_INTL ? import("./imports/with-polyfills/no") : import("./imports/no");
    case "nl":
      return SHOULD_POLYFILL_INTL ? import("./imports/with-polyfills/nl") : import("./imports/nl");
    case "pl":
      return SHOULD_POLYFILL_INTL ? import("./imports/with-polyfills/pl") : import("./imports/pl");
    case "pt-BR":
      return SHOULD_POLYFILL_INTL ? import("./imports/with-polyfills/pt-BR") : import("./imports/pt-BR");
    case "ro":
      return SHOULD_POLYFILL_INTL ? import("./imports/with-polyfills/ro") : import("./imports/ro");
    case "ru":
      return SHOULD_POLYFILL_INTL ? import("./imports/with-polyfills/ru") : import("./imports/ru");
    case "sk":
      return SHOULD_POLYFILL_INTL ? import("./imports/with-polyfills/sk") : import("./imports/sk");
    case "sv":
      return SHOULD_POLYFILL_INTL ? import("./imports/with-polyfills/sv") : import("./imports/sv");
    case "th":
      return SHOULD_POLYFILL_INTL ? import("./imports/with-polyfills/th") : import("./imports/th");
    case "tr":
      return SHOULD_POLYFILL_INTL ? import("./imports/with-polyfills/tr") : import("./imports/tr");
    case "uk":
      return SHOULD_POLYFILL_INTL ? import("./imports/with-polyfills/uk") : import("./imports/uk");
    case "zh-CN":
      return SHOULD_POLYFILL_INTL ? import("./imports/with-polyfills/zh-CN") : import("./imports/zh-CN");
    case "zh-TW":
      return SHOULD_POLYFILL_INTL ? import("./imports/with-polyfills/zh-TW") : import("./imports/zh-TW");

    default:
      throw new Error(`I18nLocaleLoader#loadLanguageModules unsupported languageCode ${languageCode}`);
  }
}
//#endregion

export class I18nLocaleLoader implements II18nLocaleLoader {
  private _options: I18nLocaleLoaderOptionsT;
  localeData: I18nLocaleDataT = {
    messages: { ...noTranslate, ...enLocale },
    locale: DEFAULT_LANGUAGE_CODE,
    timeFormatters: defaultTimeFormatters,
  };

  private constructor(options: I18nLocaleLoaderOptionsT) {
    this._options = { ...options };
  }

  static async create(options: I18nLocaleLoaderOptionsT): Promise<I18nLocaleLoader> {
    await prepareI18nDependencies();
    const localeLoader = new I18nLocaleLoader(options);

    // use in priority: previously saved language code, parsed from locale or default
    const storage = options.storage;
    let languageCode = storage?.getItem(LOCAL_STORAGE_LANGUAGE_CODE_KEY) as SupportedLanguageCodeT | null;
    if (languageCode == null || !SUPPORTED_LANGUAGE_CODES.includes(languageCode)) {
      languageCode = parseLocaleForLanguageCode(options.detectedLocale);
      storage?.removeItem(LOCAL_STORAGE_LANGUAGE_CODE_KEY);
    }

    // load data
    await localeLoader.loadLanguageMessages({ languageCode, setAsDefault: false });

    return localeLoader;
  }

  async loadLanguageMessages({ languageCode, setAsDefault }: I18nLocaleLoaderLoadLanguageMessagesOptionsT) {
    if (!SUPPORTED_LANGUAGE_CODES.includes(languageCode)) {
      throw new Error(`I18nLocaleLoader#loadLanguageMessages() unsupported language: ${languageCode}`);
    }

    if (languageCode === "en") {
      this.localeData = {
        messages: { ...noTranslate, ...enLocale },
        locale: languageCode,
        timeFormatters: defaultTimeFormatters,
      };
    } else {
      try {
        const { importedMessages, timeFormatters } = await languageImports(languageCode);
        this.localeData = {
          messages: { ...noTranslate, ...enLocale, ...importedMessages },
          locale: languageCode,
          timeFormatters: timeFormatters,
        };
      } catch (e) {
        logError(e, "I18nLocaleLoader#loadLanguageMessages()");
        throw e;
      }
    }

    if (setAsDefault) {
      this._setCurrentLanguageCode(languageCode);
    }
  }

  private _setCurrentLanguageCode(languageCode: string) {
    this._options.storage?.setItem(LOCAL_STORAGE_LANGUAGE_CODE_KEY, languageCode);
  }
}
